import type { ReactNode } from 'react';
import { useMemo } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
	createBrowserRouter,
	createRoutesFromElements,
	RouterProvider,
} from 'react-router';
import { Toaster } from '@/components/ui/sonner';

import { env } from '@/env';
import { AuthProvider } from './use-auth';

/**
 * Provider composition — order: DataRouter > AuthProvider > TooltipProvider >
 * QueryClient. `children` are the app's <Route> elements (declared in App.tsx),
 * fed to createRoutesFromElements so the data router owns the route tree.
 *
 * The DATA router (createBrowserRouter + RouterProvider) is required for the
 * `viewTransition` prop on <Link>/<NavLink> to invoke document.startViewTransition;
 * the classic <BrowserRouter> silently no-ops it.
 *
 * TooltipProvider is Radix's primitive directly (this standalone app has no
 * shadcn `@/components/ui` barrel yet — the primitive is the installed dep).
 */
export const Providers = ({ children }: { children: ReactNode }) => {
	const queryClient = new QueryClient();

	// children is static JSX from App.tsx and never changes at runtime, so an
	// empty dep array is correct — rebuilding the router would reset nav history.
	const router = useMemo(
		() => createBrowserRouter(createRoutesFromElements(children)),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	return (
		<AuthProvider>
			<Tooltip.Provider>
				<QueryClientProvider client={queryClient}>
					{(env.VITE_ENV === 'Dev' || env.VITE_ENV === 'Test') && (
						<ReactQueryDevtools initialIsOpen={false} />
					)}
					<RouterProvider router={router} />
					<Toaster />
				</QueryClientProvider>
			</Tooltip.Provider>
		</AuthProvider>
	);
};
